import React, { useEffect, useState } from 'react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { stripVariantSuffix } from '@/components/customercenter-ui/utils/formatUtils';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createRecommendClient } from '@/helpers/algolia/algoliaRecommendV5';
import { fetchAlgoliaSourceProductForRecommend } from '@/helpers/utils/fetchAlgoliaSourceProductForRecommend';
import { getRecommendAlgoliaIndexName } from '@/helpers/utils/recommendAlgoliaIndexName';
import {
  buildSimilarItemsQueryParameters,
  getSourceProductAttributesToRetrieve,
  resolveStudioAttributeValues,
} from '@/helpers/utils/similarItemsQueryParameters';
import AlgoliaRecommendProductsList from './algoliaRecommendProductsList';

interface SimilarItemsProps {
  siteId?: string;
  maxSimilarProductsPerPage?: number;
  objectId?: any;
  setProductsStatus: (haveProducts: boolean) => void;
  data: any;
  setProductsHave: (haveProducts: boolean) => void;
}

const SimilarItems: React.FC<SimilarItemsProps> = ({
  siteId,
  maxSimilarProductsPerPage,
  objectId,
  setProductsStatus,
  data,
}) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const [lookingSimilarItems, setLookingSimilarItems] = useState<any>(undefined);
  const algoliaAppId = projectSettings?.algoliaAppId;
  const algoliaApiKey = projectSettings?.algoliaApiKey;
  const attributesToFilter = projectSettings?.pdpSimilarItemsAttributesToFilter ?? [];
  const masterVariantAttributesKey = JSON.stringify(
    data?.data?.data?.dataSource?.product?.masterVariantAttributes ?? null,
  );

  useEffect(() => {
    if (!siteId || !algoliaAppId || !algoliaApiKey || !objectId) {
      setProductsStatus(false);
      return;
    }

    const recommendClient = createRecommendClient(algoliaAppId, algoliaApiKey);
    if (!recommendClient) {
      return;
    }

    const masterVariantAttributes = data?.data?.data?.dataSource?.product?.masterVariantAttributes ?? {};

    let isCancelled = false;
    const indexName = getRecommendAlgoliaIndexName(siteId, projectSettings);
    const normalizedObjectId = stripVariantSuffix(objectId);

    const fetchSimilarItems = async () => {
      try {
        const sourceProduct = await fetchAlgoliaSourceProductForRecommend({
          appId: algoliaAppId,
          apiKey: algoliaApiKey,
          indexName,
          objectId: normalizedObjectId,
          attributesToRetrieve: getSourceProductAttributesToRetrieve(attributesToFilter),
        });

        if (isCancelled) {
          return;
        }

        const attributeValues = resolveStudioAttributeValues(
          attributesToFilter,
          sourceProduct ?? {},
          masterVariantAttributes,
        );

        const similarItemsQueryParameters = buildSimilarItemsQueryParameters({
          hierarchicalCategories: sourceProduct?.hierarchicalCategories,
          categories: sourceProduct?.categories,
          attributesToFilter,
          attributeValues,
        });

        const recommendationQueries: any = [
          {
            indexName,
            objectID: normalizedObjectId,
            maxRecommendations: maxSimilarProductsPerPage ?? 15,
            threshold: 0,
            queryParameters: similarItemsQueryParameters,
          },
        ];

        const recommendations: any = await recommendClient.getRecommendations({
          requests: recommendationQueries.map((query: any) => ({
            ...query,
            model: 'looking-similar' as const,
          })),
        });

        if (isCancelled) {
          return;
        }

        if (recommendations?.results) {
          const lookingSimilarItems: any[] = [];
          recommendations.results
            ?.filter((item: any, index: any) => recommendations.results.indexOf(item) === index)
            ?.forEach((item: any) => {
              if (item?.hits && item.hits.length > 0) {
                lookingSimilarItems.push(...item.hits);
              }
            });
          setLookingSimilarItems(lookingSimilarItems);
          setProductsStatus(lookingSimilarItems.length > 0);
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }
        console.error('Error while fetching similar items:', error);
        setProductsStatus(false);
        setLookingSimilarItems(undefined);
      }
    };

    fetchSimilarItems();

    return () => {
      isCancelled = true;
    };
  }, [
    objectId,
    siteId,
    algoliaAppId,
    algoliaApiKey,
    maxSimilarProductsPerPage,
    projectSettings?.pdpSimilarItemsAttributesToFilter,
    masterVariantAttributesKey,
  ]);

  return (
    <section id="similarItems">
      {lookingSimilarItems && <AlgoliaRecommendProductsList item={lookingSimilarItems} />}
    </section>
  );
};

export default SimilarItems;
